import { useEffect, useState } from 'react';
import { payConfigGateway } from '../../pay-config';
import { payFrequencyLabels, type PayScheduleResponse } from '../../pay-config/types';
import type { CustomPayRunResponse } from '../types';

/** `2026-04-29` → `04/29/2026` (the approved design's date style). Built from the parts so no time zone can shift the day. */
export function formatUsDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : isoDate;
}

export function formatUsPeriod(run: Pick<CustomPayRunResponse, 'periodStartDate' | 'periodEndDate'>): string {
  return `${formatUsDate(run.periodStartDate)} - ${formatUsDate(run.periodEndDate)}`;
}

export interface PayScheduleInfo {
  /** Every active schedule of the company (Module 3), for the read-only Business Pay Schedule field. */
  schedules: PayScheduleResponse[];
  /** The schedule this run was created against, or null when it was created without one (or it cannot be read). */
  schedule: PayScheduleResponse | null;
  loading: boolean;
}

/** The run's own pay schedule, read from the existing pay-config gateway. The schedule (like the period and check
 * date) is fixed when the run is created — there is no edit endpoint — so this is read-only context, never an input. */
export function usePayScheduleInfo(run: CustomPayRunResponse): PayScheduleInfo {
  const [state, setState] = useState<{ schedules: PayScheduleResponse[]; loading: boolean }>({ schedules: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    payConfigGateway
      .listPaySchedules()
      .then((schedules) => {
        if (!cancelled) setState({ schedules: schedules.filter((s) => s.isActive), loading: false });
      })
      .catch(() => {
        // Context only: the run still works without the schedule name.
        if (!cancelled) setState({ schedules: [], loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const schedule = state.schedules.find((s) => s.id === run.payScheduleId) ?? null;
  return { schedules: state.schedules, schedule, loading: state.loading };
}

export function frequencyLabel(schedule: PayScheduleResponse | null): string {
  return schedule ? (payFrequencyLabels[schedule.frequency] ?? '—') : '—';
}
